import { useState, useEffect, useCallback } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { ArrowLeft, UserPlus, Users } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { fetchUsers, adminInviteUser, adminUpdateUser } from "@/lib/api";
import AssignmentFocus from "@/components/users/AssignmentFocus";

const ROLES = ["ADMIN", "MEMBER"];

function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function AdminUsersPage({ currentUser, onUsersChanged }) {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [invite, setInvite] = useState({
    full_name: "",
    email: "",
    password: "",
    role: "MEMBER",
  });
  const [inviting, setInviting] = useState(false);

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      const data = await fetchUsers();
      setUsers(data);
    } catch (e) {
      toast.error("Failed to load users");
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!invite.full_name.trim() || !invite.email.trim() || !invite.password) {
      toast.error("Fill in name, email, and password");
      return;
    }
    try {
      setInviting(true);
      await adminInviteUser({
        full_name: invite.full_name.trim(),
        email: invite.email.trim(),
        password: invite.password,
        role: invite.role,
      });
      toast.success("User invited");
      setInvite({ full_name: "", email: "", password: "", role: "MEMBER" });
      setInviteOpen(false);
      await loadUsers();
      onUsersChanged?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to invite user");
    } finally {
      setInviting(false);
    }
  };

  const handleRoleChange = async (userId, role) => {
    try {
      setSavingId(userId);
      const updated = await adminUpdateUser(userId, { role });
      setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, ...updated } : u)));
      onUsersChanged?.();
      toast.success("User updated");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to update user");
    } finally {
      setSavingId(null);
    }
  };

  if (currentUser?.role !== "ADMIN") {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="h-full overflow-hidden" data-testid="admin-users-page">
      <ScrollArea className="h-full">
        <div className="max-w-4xl mx-auto p-8 space-y-8">
          <div className="flex items-center gap-3">
            <button
              data-testid="admin-users-back"
              onClick={() => navigate(-1)}
              className="p-1.5 hover:bg-gray-100 rounded-sm transition-colors"
            >
              <ArrowLeft className="w-4 h-4 text-gray-500" />
            </button>
            <div>
              <h1
                className="text-2xl font-black tracking-tight"
                style={{ fontFamily: "Chivo, sans-serif" }}
              >
                Users
              </h1>
              <p className="text-sm text-gray-500 mt-0.5">
                Invite members and manage roles. Assignment focus is derived from issue history.
              </p>
            </div>
          </div>

          <p className="text-xs text-gray-500">
            {users.length} user{users.length !== 1 ? "s" : ""} total
          </p>

          <div className="border border-gray-200 rounded-sm bg-white overflow-hidden">
            <button
              type="button"
              data-testid="admin-users-invite-toggle"
              onClick={() => setInviteOpen((o) => !o)}
              className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50 transition-colors text-left"
            >
              <span className="flex items-center gap-2 text-sm font-bold" style={{ fontFamily: "Chivo, sans-serif" }}>
                <UserPlus className="w-4 h-4" />
                Invite user
              </span>
              <span className="text-xs text-gray-500">{inviteOpen ? "Hide" : "Show"}</span>
            </button>
            {inviteOpen && (
              <form onSubmit={handleInvite} className="px-5 pb-5 pt-0 space-y-4 border-t border-gray-100">
                <div className="grid sm:grid-cols-2 gap-4 pt-4">
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500">
                      Full name
                    </label>
                    <input
                      data-testid="invite-full-name"
                      type="text"
                      value={invite.full_name}
                      onChange={(e) => setInvite((p) => ({ ...p, full_name: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-200 rounded-sm text-sm focus:border-brand-rose focus:ring-1 focus:ring-brand-rose outline-none"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500">
                      Email
                    </label>
                    <input
                      data-testid="invite-email"
                      type="email"
                      value={invite.email}
                      onChange={(e) => setInvite((p) => ({ ...p, email: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-200 rounded-sm text-sm focus:border-brand-rose focus:ring-1 focus:ring-brand-rose outline-none"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500">
                      Password
                    </label>
                    <input
                      data-testid="invite-password"
                      type="password"
                      value={invite.password}
                      onChange={(e) => setInvite((p) => ({ ...p, password: e.target.value }))}
                      placeholder="Upper, lower, digit, 8+ chars"
                      className="w-full px-3 py-2 border border-gray-200 rounded-sm text-sm focus:border-brand-rose focus:ring-1 focus:ring-brand-rose outline-none"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500">
                      Role
                    </label>
                    <Select
                      value={invite.role}
                      onValueChange={(v) => setInvite((p) => ({ ...p, role: v }))}
                    >
                      <SelectTrigger className="rounded-sm h-10 text-sm max-w-xs" data-testid="invite-role">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="rounded-sm">
                        {ROLES.map((r) => (
                          <SelectItem key={r} value={r} className="rounded-sm text-sm">
                            {r}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <button
                  type="submit"
                  data-testid="invite-submit"
                  disabled={inviting}
                  className="bg-brand text-white hover:bg-brand-dark rounded-sm px-4 py-2 text-sm font-semibold transition-colors disabled:opacity-40"
                >
                  {inviting ? "Creating..." : "Create user"}
                </button>
              </form>
            )}
          </div>

          <div className="space-y-3">
            <h3 className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500 flex items-center gap-2">
              <Users className="w-3.5 h-3.5" />
              All users
            </h3>
            {loading ? (
              <p className="text-sm text-gray-500">Loading users...</p>
            ) : (
              <div className="border border-gray-200 rounded-sm bg-white divide-y divide-gray-100">
                {users.map((user) => (
                  <div
                    key={user.id}
                    data-testid={`admin-user-row-${user.id}`}
                    className={`flex flex-wrap items-center gap-3 px-4 py-3 ${
                      user.id === currentUser.id ? "bg-gray-50" : ""
                    }`}
                  >
                    <Avatar className="h-9 w-9 border border-gray-200 flex-shrink-0">
                      <AvatarImage src={user.avatar_url} alt={user.full_name} />
                      <AvatarFallback className="text-xs font-semibold">
                        {user.full_name?.charAt(0)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-[160px]">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold">{user.full_name}</span>
                        {user.id === currentUser.id && (
                          <span className="text-[10px] font-bold text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded-sm">
                            YOU
                          </span>
                        )}
                      </div>
                      <span className="text-xs text-gray-500">{user.email}</span>
                      <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-gray-400 mt-1">
                        Often assigned to
                      </p>
                      <AssignmentFocus
                        labels={user.assignment_focus}
                        className="text-xs text-gray-600"
                        emptyText="No assignments yet"
                      />
                    </div>
                    <Select
                      value={user.role || "MEMBER"}
                      disabled={savingId === user.id}
                      onValueChange={(role) => handleRoleChange(user.id, role)}
                    >
                      <SelectTrigger
                        className="w-[110px] rounded-sm h-9 text-xs"
                        data-testid={`user-role-${user.id}`}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="rounded-sm">
                        {ROLES.map((r) => (
                          <SelectItem key={r} value={r} className="rounded-sm text-xs">
                            {r}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </ScrollArea>
    </div>
  );
}
